import MuleBadge from '../../components/MuleBadge';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import MultiSelect from '../../components/MultiSelect';
import Sparkline, {
  SparklinePoint,
  computeSparklineMax,
} from '../../components/Sparkline';
import TagBadge from '../../components/TagBadge';
import { Tooltip } from '../../components/ui/Tooltip';
import rejectionAnalyticsService, {
  AnalyticsFilters,
  ByDriverRow,
  ByLocationRow,
  ByReasonRow,
  HotSpotRow,
  RejectionRow,
  SummaryResponse,
  WindowDays,
} from '../../services/rejectionAnalytics.service';

type TabKey = 'driver' | 'location' | 'reason' | 'hotspots' | 'rejections';

const ALLOWED_DAYS: WindowDays[] = [30, 60, 90, 180];

const toTitleCase = (s: string | undefined | null) => {
  if (!s) return '';
  return s
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const formatCurrency = (n: number | null | undefined) =>
  n != null
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';

const formatNumber = (n: number | null | undefined) =>
  n != null ? n.toLocaleString() : '—';

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '—';

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

const rejectRateColorClass = (pct: number) => {
  if (pct >= 10) return 'text-red-700 dark:text-red-400 font-medium';
  if (pct >= 5) return 'text-amber-700 dark:text-amber-400 font-medium';
  return 'text-emerald-700 dark:text-emerald-400 font-medium';
};

const RejectionAnalytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven state
  const activeTab: TabKey = useMemo(() => {
    const t = searchParams.get('tab');
    if (
      t === 'location' ||
      t === 'reason' ||
      t === 'hotspots' ||
      t === 'rejections'
    ) {
      return t;
    }
    return 'driver';
  }, [searchParams]);

  const windowDays: WindowDays = useMemo(() => {
    const d = Number(searchParams.get('days'));
    return (ALLOWED_DAYS as number[]).includes(d) ? (d as WindowDays) : 90;
  }, [searchParams]);

  const regionFilter = useMemo(() => {
    const r = searchParams.get('regions');
    return r ? r.split(',').filter(Boolean) : [];
  }, [searchParams]);

  const driverFilter = searchParams.get('driver') || '';
  const locationFilter = searchParams.get('location_id') || '';
  const reasonFilter = searchParams.get('reason') || '';

  const filters: AnalyticsFilters = useMemo(
    () => ({
      days: windowDays,
      regions: regionFilter.length > 0 ? regionFilter : undefined,
    }),
    [windowDays, regionFilter]
  );

  // Update a URL param (or remove if empty)
  const updateParam = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---- Data fetching ----
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [byDriver, setByDriver] = useState<ByDriverRow[]>([]);
  const [byLocation, setByLocation] = useState<ByLocationRow[]>([]);
  const [byReason, setByReason] = useState<ByReasonRow[]>([]);
  const [hotSpots, setHotSpots] = useState<HotSpotRow[]>([]);
  const [rejections, setRejections] = useState<RejectionRow[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  // Summary refetches on every filter change. KPI cards + sparkline
  // also follow the chip filters (driver / location / reason) so the
  // header numbers reflect the same scope as the active tab.
  const summaryFilters = useMemo<AnalyticsFilters>(
    () => ({
      ...filters,
      driver: driverFilter || undefined,
      location_id: locationFilter ? Number(locationFilter) : undefined,
      reason: reasonFilter || undefined,
    }),
    [filters, driverFilter, locationFilter, reasonFilter]
  );

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    rejectionAnalyticsService
      .summary(summaryFilters)
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch((e) => console.error('Error loading summary:', e))
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summaryFilters]);

  // Active tab refetches when the tab or filters change
  useEffect(() => {
    let cancelled = false;
    setTabLoading(true);
    const tabFilters: AnalyticsFilters = {
      ...filters,
      driver: activeTab === 'rejections' ? driverFilter || undefined : undefined,
      location_id:
        activeTab === 'rejections' && locationFilter
          ? Number(locationFilter)
          : undefined,
      reason:
        activeTab === 'rejections' ? reasonFilter || undefined : undefined,
    };
    const fetcher =
      activeTab === 'driver'
        ? rejectionAnalyticsService.byDriver(tabFilters).then((res) => {
            if (!cancelled) setByDriver(res);
          })
        : activeTab === 'location'
        ? rejectionAnalyticsService.byLocation(tabFilters).then((res) => {
            if (!cancelled) setByLocation(res);
          })
        : activeTab === 'reason'
        ? rejectionAnalyticsService.byReason(tabFilters).then((res) => {
            if (!cancelled) setByReason(res);
          })
        : activeTab === 'hotspots'
        ? rejectionAnalyticsService.hotSpots(tabFilters).then((res) => {
            if (!cancelled) setHotSpots(res);
          })
        : rejectionAnalyticsService.rejections(tabFilters).then((res) => {
            if (!cancelled) setRejections(res);
          });
    fetcher
      .catch((e) => console.error('Error loading tab data:', e))
      .finally(() => {
        if (!cancelled) setTabLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, filters, driverFilter, locationFilter, reasonFilter]);

  // ---- Cross-filter helpers ----
  const drillToDriver = useCallback(
    (driver: string) =>
      updateParam({ tab: 'rejections', driver, location_id: null, reason: null }),
    [updateParam]
  );
  const drillToLocation = useCallback(
    (locationId: number) =>
      updateParam({
        tab: 'rejections',
        location_id: String(locationId),
        driver: null,
        reason: null,
      }),
    [updateParam]
  );
  const drillToReason = useCallback(
    (reason: string) =>
      updateParam({ tab: 'rejections', reason, driver: null, location_id: null }),
    [updateParam]
  );
  const drillToDriverAtLocation = useCallback(
    (driver: string, locationId: number) =>
      updateParam({
        tab: 'rejections',
        driver,
        location_id: String(locationId),
        reason: null,
      }),
    [updateParam]
  );

  // ---- Region options (data-driven from summary) ----
  const regionOptions = useMemo(
    () =>
      (summary?.available_regions || []).map((r) => ({ value: r, label: r })),
    [summary]
  );

  // ---- Sparkline data ----
  // Weekends are filtered out — dispatch volume drops sharply on Sat/Sun,
  // making the trend visually noisier than informative. Aggregate KPI
  // totals still include weekend rows.
  const sparklinePoints: SparklinePoint[] = useMemo(() => {
    if (!summary) return [];
    return summary.trend
      .filter((t) => {
        // Parse YYYY-MM-DD as local time so getDay() reflects the calendar
        // day, not the UTC-midnight shift.
        const [y, m, d] = t.date.split('-').map(Number);
        if (!y || !m || !d) return true;
        const dow = new Date(y, m - 1, d).getDay();
        return dow !== 0 && dow !== 6;
      })
      .map((t) => {
        const total = t.moves + t.rejections;
        const rate = total ? Math.round((t.rejections / total) * 1000) / 10 : 0;
        const [y, m, d] = t.date.split('-').map(Number);
        const friendlyDate =
          y && m && d
            ? new Date(y, m - 1, d).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })
            : t.date;
        return {
          date: t.date,
          value: t.rejections,
          // Green line = total dispatches that day (accepts + rejects), so
          // the volume reads on every day that had any activity, not just
          // days with successful accepts.
          secondary: total,
          label: `${friendlyDate}: ${t.rejections} rejections of ${total} dispatches (${rate}%)`,
        };
      });
  }, [summary]);

  const sparklineMax = useMemo(
    () => computeSparklineMax(sparklinePoints),
    [sparklinePoints]
  );

  // ---- Column defs ----
  const byDriverColumns = useMemo<ColumnDef<ByDriverRow>[]>(
    () => [
      {
        accessorKey: 'driver',
        meta: { exportValue: (row: { original: ByDriverRow }) => `${row.original.driver}${row.original.isMule ? ' (Mule)' : ''}` },
        header: 'Driver',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <button
              type="button"
              onClick={() => drillToDriver(row.driver)}
              className="text-blue-600 dark:text-blue-400 hover:underline text-left tabular-nums"
            >
              {row.driver}<MuleBadge isMule={row.isMule} />
              {row.terminated && (
                <span className="ml-1 text-[0.625rem] text-muted">
                  (terminated)
                </span>
              )}
            </button>
          );
        },
      },
      {
        accessorKey: 'driver_name',
        header: 'Name',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => toTitleCase(info.getValue<string>()),
      },
      {
        accessorKey: 'rejects',
        header: 'Rejects',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'accepts',
        header: 'Accepts',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'total',
        header: 'Total',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'reject_rate',
        header: 'Reject Rate',
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue<number>();
          return (
            <span className={`tabular-nums ${rejectRateColorClass(v)}`}>
              {v.toFixed(1)}%
            </span>
          );
        },
      },
      {
        accessorKey: 'top_reason',
        header: 'Top Reason',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.getValue<string>() || '—',
      },
      {
        accessorKey: 'locations_rejected_at',
        header: 'Locations',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'rejected_pay_total',
        header: 'Pay on Rejects',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatCurrency(info.getValue<number>())}</span>
        ),
      },
    ],
    [drillToDriver]
  );

  const byLocationColumns = useMemo<ColumnDef<ByLocationRow>[]>(
    () => [
      {
        accessorKey: 'location_name',
        header: 'Location',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <button
              type="button"
              onClick={() => drillToLocation(row.location_id)}
              className="text-blue-600 dark:text-blue-400 hover:underline text-left"
            >
              {row.location_name}
            </button>
          );
        },
      },
      {
        accessorKey: 'location_state',
        header: 'State',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.getValue<string>() || '—',
      },
      {
        accessorKey: 'location_city',
        header: 'City',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.getValue<string>() || '—',
      },
      {
        accessorKey: 'rejects',
        header: 'Rejects',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'accepts',
        header: 'Accepts',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'total',
        header: 'Total',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'reject_rate',
        header: 'Reject Rate',
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue<number>();
          return (
            <span className={`tabular-nums ${rejectRateColorClass(v)}`}>
              {v.toFixed(1)}%
            </span>
          );
        },
      },
      {
        accessorKey: 'top_reason',
        header: 'Top Reason',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.getValue<string>() || '—',
      },
      {
        accessorKey: 'distinct_drivers_rejecting',
        header: 'Drivers',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'avg_rejected_pay',
        header: 'Avg Pay on Rejects',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">
            {formatCurrency(info.getValue<number | null>())}
          </span>
        ),
      },
    ],
    [drillToLocation]
  );

  const byReasonColumns = useMemo<ColumnDef<ByReasonRow>[]>(
    () => [
      {
        accessorKey: 'reason',
        header: 'Reason',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const reason = info.getValue<string>();
          return (
            <button
              type="button"
              onClick={() => drillToReason(reason)}
              className="text-blue-600 dark:text-blue-400 hover:underline text-left"
            >
              {reason}
            </button>
          );
        },
      },
      {
        accessorKey: 'count',
        header: 'Count',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'distinct_drivers',
        header: 'Drivers',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'distinct_locations',
        header: 'Locations',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatNumber(info.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'last_seen',
        header: 'Last Seen',
        enableSorting: true,
        cell: (info) => (
          <span className="whitespace-nowrap">
            {formatDate(info.getValue<string | null>())}
          </span>
        ),
      },
      {
        accessorKey: 'rejected_pay_total',
        header: 'Pay on Rejects',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">{formatCurrency(info.getValue<number>())}</span>
        ),
      },
    ],
    [drillToReason]
  );

  const hotSpotsColumns = useMemo<ColumnDef<HotSpotRow>[]>(
    () => [
      {
        accessorKey: 'location_name',
        header: 'Location',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="block self-start">
              <button
                type="button"
                onClick={() => drillToLocation(row.location_id)}
                className="text-blue-600 dark:text-blue-400 hover:underline text-left"
              >
                {row.location_name}
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'location_state',
        header: 'State',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => (
          <div className="block self-start">{info.getValue<string>() || '—'}</div>
        ),
      },
      {
        accessorKey: 'location_city',
        header: 'City',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => (
          <div className="block self-start">{info.getValue<string>() || '—'}</div>
        ),
      },
      {
        accessorKey: 'rejects',
        header: 'Rejects',
        enableSorting: true,
        cell: (info) => (
          <div className="block self-start tabular-nums">
            {formatNumber(info.getValue<number>())}
          </div>
        ),
      },
      {
        accessorKey: 'reject_rate',
        header: 'Reject Rate',
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue<number>();
          return (
            <div className={`block self-start tabular-nums ${rejectRateColorClass(v)}`}>
              {v.toFixed(1)}%
            </div>
          );
        },
      },
      {
        id: 'top_rejecters',
        header: 'Top Rejecters',
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          if (row.top_rejecters.length === 0) {
            return <span className="text-gray-400">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-0.5">
              {row.top_rejecters.map((d) => {
                const fullName = (d.driver_name || '').trim();
                const label = `${d.driver}${d.isMule ? " (Mule)" : ""}${
                  fullName ? ` - ${toTitleCase(fullName)}` : ''
                } · ${d.rejects}/${d.accepts} - ${d.reject_rate.toFixed(0)}%`;
                return (
                  <Tooltip
                    key={d.driver}
                    content={
                      <div className="text-xs text-gray-800 dark:text-gray-100">
                        <div className="font-medium">
                          {d.driver}<MuleBadge isMule={d.isMule} />
                          {fullName ? ` - ${toTitleCase(fullName)}` : ''}
                          {d.terminated && (
                            <span className="ml-1 text-[0.625rem] text-muted">
                              (terminated)
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-amber-700 dark:text-amber-300 font-medium">
                          {d.rejects} reject{d.rejects === 1 ? '' : 's'} /{' '}
                          {d.accepts} accept{d.accepts === 1 ? '' : 's'}{' '}
                          ({d.reject_rate.toFixed(1)}% reject rate)
                        </div>
                        <div className="mt-1 text-muted italic">
                          Click for this driver's rejections at this location
                        </div>
                      </div>
                    }
                    placement="top"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        drillToDriverAtLocation(d.driver, row.location_id)
                      }
                      className="appearance-none border-0 bg-transparent p-0 m-0 text-left cursor-pointer"
                    >
                      <TagBadge
                        label={label}
                        color="yellow"
                        className="whitespace-nowrap"
                      />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          );
        },
      },
    ],
    [drillToLocation, drillToDriverAtLocation]
  );

  const rejectionsColumns = useMemo<ColumnDef<RejectionRow>[]>(
    () => [
      {
        accessorKey: 'rejectdatetime',
        header: 'Date',
        enableSorting: true,
        cell: (info) => (
          <span className="whitespace-nowrap">
            {formatDateTime(info.getValue<string | null>())}
          </span>
        ),
      },
      {
        accessorKey: 'pronumber',
        header: 'Pro #',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">
            {info.getValue<number | null>() ?? '—'}
          </span>
        ),
      },
      {
        id: 'driver_full',
        meta: { exportValue: (row: { original: RejectionRow }) => `${row.original.driver} - ${row.original.driver_name}${row.original.isMule ? ' (Mule)' : ''}` },
        header: 'Driver',
        accessorFn: (row) =>
          `${row.driver}${row.driver_name ? ` - ${toTitleCase(row.driver_name)}` : ''}`,
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          return (
            <span className="whitespace-nowrap">
              {row.driver}<MuleBadge isMule={row.isMule} />
              {row.driver_name && ` - ${toTitleCase(row.driver_name)}`}
              {row.terminated && (
                <span className="ml-1 text-[0.625rem] text-muted">
                  (terminated)
                </span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'origin_name',
        header: 'Origin',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          const name = info.getValue<string>();
          if (!name) return '—';
          return (
            <button
              type="button"
              onClick={() => drillToLocation(row.origin_id)}
              className="text-blue-600 dark:text-blue-400 hover:underline text-left"
            >
              {name}
            </button>
          );
        },
      },
      {
        accessorKey: 'destination_name',
        header: 'Destination',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original;
          const name = info.getValue<string>();
          if (!name) return '—';
          return (
            <button
              type="button"
              onClick={() => drillToLocation(row.destination_id)}
              className="text-blue-600 dark:text-blue-400 hover:underline text-left"
            >
              {name}
            </button>
          );
        },
      },
      {
        accessorKey: 'rejectreason',
        header: 'Reason',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.getValue<string>() || '—',
      },
      {
        accessorKey: 'rejectcomment',
        header: 'Comment',
        cell: (info) => info.getValue<string>() || '',
      },
      {
        accessorKey: 'estimated_driver_pay',
        header: 'Est. Pay',
        enableSorting: true,
        cell: (info) => (
          <span className="tabular-nums">
            {formatCurrency(info.getValue<number | null>())}
          </span>
        ),
      },
    ],
    [drillToLocation]
  );

  // Locate the cross-filter context for the chip strip. Look in any of
  // the tabs' data we've already loaded — whichever tab the drill came
  // from is the most likely to have the name.
  const filteredLocationName = useMemo(() => {
    if (!locationFilter) return '';
    const id = Number(locationFilter);
    const fromLocation = byLocation.find((l) => l.location_id === id)?.location_name;
    if (fromLocation) return fromLocation;
    const fromHotSpots = hotSpots.find((h) => h.location_id === id)?.location_name;
    if (fromHotSpots) return fromHotSpots;
    for (const r of rejections) {
      if (r.origin_id === id && r.origin_name) return r.origin_name;
      if (r.destination_id === id && r.destination_name) return r.destination_name;
    }
    return '';
  }, [locationFilter, byLocation, hotSpots, rejections]);

  const filteredDriverName = useMemo(() => {
    if (!driverFilter) return '';
    const d = byDriver.find((row) => row.driver === driverFilter);
    return d?.driver_name ? toTitleCase(d.driver_name) : '';
  }, [driverFilter, byDriver]);

  return (
    <div className="flex-1 flex flex-col m-[10px] min-h-0 gap-2">
      {/* Filter bar */}
      <div className="flex items-center gap-3 p-2 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg">
        <div className="w-32">
          <div className="relative">
            <select
              value={windowDays}
              onChange={(e) => updateParam({ days: e.target.value })}
              className="appearance-none w-full py-1 ps-2 pe-6 text-xs bg-white dark:bg-gray-900 border border-gray-400 dark:border-gray-500 rounded-lg text-heading focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600 cursor-pointer"
            >
              {ALLOWED_DAYS.map((d) => (
                <option key={d} value={d}>
                  Last {d} days
                </option>
              ))}
            </select>
            <div className="absolute top-1/2 end-1 -translate-y-1/2 pointer-events-none">
              <ChevronUpDownIcon className="w-3 h-3 text-gray-500 dark:text-gray-500" />
            </div>
          </div>
        </div>
        <div className="w-56">
          <MultiSelect
            options={regionOptions}
            value={regionFilter}
            onChange={(next) =>
              updateParam({ regions: next.length > 0 ? next.join(',') : null })
            }
            placeholder="All regions"
            showClearOption={true}
            clearOptionLabel="All"
            enableSearch={true}
            searchPlaceholder="Filter regions…"
            size="sm"
            className="w-full"
          />
        </div>
        <div className="flex-1" />
        {regionFilter.length > 0 && (
          <button
            type="button"
            onClick={() => updateParam({ regions: null })}
            className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* KPI strip + sparkline */}
      <div className="bg-panel border border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard
            label="Moves"
            value={formatNumber(summary?.total_moves)}
            loading={summaryLoading}
          />
          <KpiCard
            label="Rejections"
            value={formatNumber(summary?.total_rejections)}
            loading={summaryLoading}
            secondary={
              summary && summary.total_moves + summary.total_rejections > 0
                ? `${summary.reject_rate.toFixed(1)}% of all dispatches`
                : undefined
            }
          />
          <KpiCard
            label="Reject Rate"
            value={summary ? `${summary.reject_rate.toFixed(1)}%` : '—'}
            valueClassName={rejectRateColorClass(summary?.reject_rate ?? 0)}
            loading={summaryLoading}
          />
          <KpiCard
            label="Pay on Rejected Legs"
            value={formatCurrency(summary?.rejected_pay_total)}
            loading={summaryLoading}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[0.625rem] uppercase tracking-wide text-muted flex items-center gap-2">
            Rejections by day — last {windowDays} days (weekdays only)
            {summaryLoading && (
              <svg
                className="animate-spin h-3 w-3 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
          </div>
          {sparklinePoints.length > 0 && (
            <div className="text-[0.625rem] text-muted flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="inline-block w-2.5 h-[2px] bg-blue-600 dark:bg-blue-400" />
                <span>
                  Rejections
                  <span className="text-gray-400 dark:text-gray-500">
                    {` (max ${sparklineMax.primaryMax})`}
                  </span>
                </span>
              </span>
              {sparklineMax.hasSecondary && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-[2px] bg-emerald-600 dark:bg-emerald-400" />
                  <span>
                    Volume
                    <span className="text-gray-400 dark:text-gray-500">
                      {` (max ${sparklineMax.secondaryMax})`}
                    </span>
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        {summaryLoading && sparklinePoints.length === 0 ? (
          <div className="h-[120px] w-full bg-gray-100 dark:bg-gray-900/40 rounded animate-pulse" />
        ) : (
          <Sparkline points={sparklinePoints} height={120} />
        )}
      </div>

      {/* Tabs */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-1 border-b border-gray-300 dark:border-gray-600">
          {(
            [
              { key: 'driver', label: 'By Driver' },
              { key: 'location', label: 'By Location' },
              { key: 'reason', label: 'By Reason' },
              { key: 'hotspots', label: 'Hot Spots' },
              { key: 'rejections', label: 'All Rejections' },
            ] as { key: TabKey; label: string }[]
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => updateParam({ tab: t.key })}
              className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${
                activeTab === t.key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Cross-filter chip strip (All Rejections only) */}
        {activeTab === 'rejections' &&
          (driverFilter || locationFilter || reasonFilter) && (
            <div className="flex flex-wrap items-center gap-2 px-1 py-2 text-xs">
              <span className="text-muted">Filters:</span>
              {driverFilter && (
                <FilterChip
                  label={`Driver: ${driverFilter}${
                    filteredDriverName ? ` - ${filteredDriverName}` : ''
                  }`}
                  onClear={() => updateParam({ driver: null })}
                />
              )}
              {locationFilter && (
                <FilterChip
                  label={`Location: ${
                    filteredLocationName || `#${locationFilter}`
                  }`}
                  onClear={() => updateParam({ location_id: null })}
                />
              )}
              {reasonFilter && (
                <FilterChip
                  label={`Reason: ${reasonFilter}`}
                  onClear={() => updateParam({ reason: null })}
                />
              )}
              <button
                type="button"
                onClick={() =>
                  updateParam({ driver: null, location_id: null, reason: null })
                }
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
              >
                Clear all
              </button>
            </div>
          )}

        <div className="flex-1 min-h-0 pt-2">
          {activeTab === 'driver' && (
            <TanTable
              data={byDriver}
              columns={byDriverColumns}
              loading={tabLoading}
              cardTitle={`By Driver — ${formatNumber(byDriver.length)} drivers`}
              settingsKey="rejection-analytics-by-driver"
              fieldName="rejection-analytics-by-driver"
              enableColumnFilters={true}
              enableSearch={true}
              enableReset={true}
              enableShowHideCols={true}
              enableExcelExport={true}
              enableZebraStriping={true}
              enableGrouping={true}
            />
          )}
          {activeTab === 'location' && (
            <TanTable
              data={byLocation}
              columns={byLocationColumns}
              loading={tabLoading}
              cardTitle={`By Location — ${formatNumber(byLocation.length)} locations`}
              settingsKey="rejection-analytics-by-location"
              fieldName="rejection-analytics-by-location"
              enableColumnFilters={true}
              enableSearch={true}
              enableReset={true}
              enableShowHideCols={true}
              enableExcelExport={true}
              enableZebraStriping={true}
              enableGrouping={true}
            />
          )}
          {activeTab === 'reason' && (
            <TanTable
              data={byReason}
              columns={byReasonColumns}
              loading={tabLoading}
              cardTitle={`By Reason — ${formatNumber(byReason.length)} reasons`}
              settingsKey="rejection-analytics-by-reason"
              fieldName="rejection-analytics-by-reason"
              enableColumnFilters={true}
              enableSearch={true}
              enableReset={true}
              enableShowHideCols={true}
              enableExcelExport={true}
              enableZebraStriping={true}
              enableGrouping={true}
            />
          )}
          {activeTab === 'hotspots' && (
            <TanTable
              data={hotSpots}
              columns={hotSpotsColumns}
              loading={tabLoading}
              cardTitle={`Hot Spots — ${formatNumber(hotSpots.length)} locations`}
              settingsKey="rejection-analytics-hot-spots"
              fieldName="rejection-analytics-hot-spots"
              enableColumnFilters={true}
              enableSearch={true}
              enableReset={true}
              enableShowHideCols={true}
              enableExcelExport={true}
              enableZebraStriping={true}
              enableGrouping={true}
            />
          )}
          {activeTab === 'rejections' && (
            <TanTable
              data={rejections}
              columns={rejectionsColumns}
              loading={tabLoading}
              cardTitle={`All Rejections — ${formatNumber(rejections.length)} legs`}
              settingsKey="rejection-analytics-rejections"
              fieldName="rejection-analytics-rejections"
              enableColumnFilters={true}
              enableSearch={true}
              enableReset={true}
              enableShowHideCols={true}
              enableExcelExport={true}
              enableZebraStriping={true}
              enableGrouping={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: string;
  secondary?: string;
  loading?: boolean;
  valueClassName?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  secondary,
  loading,
  valueClassName,
}) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-900/40">
    <div className="text-[0.625rem] uppercase tracking-wide text-muted">
      {label}
    </div>
    {loading ? (
      <>
        <div className="h-6 mt-1 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-2 mt-2 w-16 bg-gray-200/70 dark:bg-gray-700/70 rounded animate-pulse" />
      </>
    ) : (
      <div
        className={`text-xl tabular-nums ${
          valueClassName ?? 'text-body'
        }`}
      >
        {value}
      </div>
    )}
    {secondary && !loading && (
      <div className="text-[0.625rem] text-muted">
        {secondary}
      </div>
    )}
  </div>
);

interface FilterChipProps {
  label: string;
  onClear: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, onClear }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
    <span>{label}</span>
    <button
      type="button"
      onClick={onClear}
      className="hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-full"
      aria-label={`Remove filter ${label}`}
    >
      <XMarkIcon className="w-3.5 h-3.5" />
    </button>
  </span>
);

export default RejectionAnalytics;
